import React, { useEffect } from "react";
import { useRef } from "react";
import { FileFieldProps } from "./FileField";
import {
  UploaderComponent,
  UploaderModel,
  UploadingEventArgs,
  RemovingEventArgs,
} from "@syncfusion/ej2-react-inputs";
import { FileDocument, buildFileDocumentCustomFormData } from "./types";
import { isNil } from "lodash";

/**
 * Custom hook to implement file field functionality
 */
export const useFileField = (props: FileFieldProps) => {
  const [authRequestHeader, setAuthRequestHeader] = React.useState<
    Record<string, string> | undefined
  >();

  const fileFieldRef = useRef<UploaderComponent>(null);
  useEffect(() => {
    setToken();
  }, []);

  const setToken = async () => {
    if (!!props.getAuthRequestHeader) {
      const token = await props.getAuthRequestHeader();
      setAuthRequestHeader(token);
    }
  };

  const onUploadingDefault = async (args: UploadingEventArgs) => {
    if (!args?.currentRequest) {
      return;
    }

    // Set Default Custom Data
    const doc: FileDocument = {
      originalFileName: args.fileData.name,
      folderName: props.documentsFolderName || "documents",
      isChunked: true,
    };

    let customData = { ...doc };
    if (props.onCustomFormData) {
      customData = {
        ...customData,
        ...props.onCustomFormData(),
      };
    }

    args.customFormData = buildFileDocumentCustomFormData(customData);

    // Overwrite as needed
    !!props.onUploading && props.onUploading(args);

    // Set Header
    setRequestHeader(args?.currentRequest);
  };

  const onChunkUploadingDefault = async (args: UploadingEventArgs) => {
    if (!args?.currentRequest) {
      return;
    }

    // Set Default Custom Data
    const doc: FileDocument = {
      id: props.documentId || args.fileData.id,
      originalFileName: args.fileData.name,
      folderName: props.documentsFolderName || "documents",
      isChunked: true,
    };

    let customData = { ...doc };
    if (props.onCustomFormData) {
      customData = {
        ...customData,
        ...props.onCustomFormData(),
      };
    }

    args.customFormData = buildFileDocumentCustomFormData(customData);

    // Overwrite as needed
    !!props.onChunkUploading && props.onChunkUploading(args);

    // Set Header
    setRequestHeader(args?.currentRequest);
  };

  const onRemovingDefault = async (args: RemovingEventArgs) => {
    if (!args?.currentRequest) {
      return;
    }

    // Set Default Custom Data
    const fileData = args.filesData[0];
    const doc: FileDocument = {
      id: props.documentId || fileData.id || fileData.size.toString(), // size is used as id on edge cases
      originalFileName: fileData.name,
      folderName: props.documentsFolderName || "documents",
      isChunked: true,
    };

    let customData = { ...doc };
    if (props.onCustomFormData) {
      customData = {
        ...customData,
        ...props.onCustomFormData(),
      };
    }

    args.customFormData = buildFileDocumentCustomFormData(customData);

    // Overwrite as needed
    !!props.onRemoving && props.onRemoving(args);

    // Set Header
    setRequestHeader(args?.currentRequest);
  };

  const onChunkSuccess = (args: any) => {
    const docOperation = JSON.parse(args.event?.target?.response);

    if (docOperation?.id) {
      args.file.id = docOperation.id;
    }
  };

  const onSuccess = (args: any) => {
    const docOperation =
      args.operation === "upload" || args.operation === "remove"
        ? JSON.parse(args.e.target.response)
        : {};

    // Set Id to the file
    args.file.id = docOperation.id;

    props.onSubmit && props.onSubmit(docOperation);
    if (args.operation === "upload") {
      props.onCompleteUploading && props.onCompleteUploading(docOperation);
    } else if (args.operation === "remove") {
      props.onCompleteRemoving && props.onCompleteRemoving(docOperation);
    }
  };

  const setRequestHeader = (currentRequest?: XMLHttpRequest) => {
    if (!currentRequest || !authRequestHeader) {
      return;
    }
    for (const key in authRequestHeader) {
      if (authRequestHeader.hasOwnProperty(key)) {
        const value = authRequestHeader[key];
        currentRequest.setRequestHeader(key, value);
      }
    }
  };

  // Config
  const defaultUploaderConfig = {
    enabled: !props.disabled,
    success: onSuccess,
    chunkSuccess: onChunkSuccess,
    uploading: onUploadingDefault,
    chunkUploading: onChunkUploadingDefault,
    removing: onRemovingDefault,
    actionComplete: props.onAllActionComplete,
    multiple: isNil(props.allowMultiple)
      ? props.uploaderConfig?.multiple
      : props.allowMultiple,
  } as UploaderModel;

  const resolvedUploaderConfig = {
    ...defaultUploaderConfig,
    ...props.uploaderConfig,
  };

  return { fileFieldRef, resolvedUploaderConfig };
};
